/* eslint-disable no-console */
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiConnectionsModel, WorkspaceRepository } from '@database/central';
import { ApiExporterService } from '@domain/api-export';
import { BusinessLayerService, IModal } from '@domain/business-layer';
import { QueryParamsProvider } from '@domain/routing';
import { NavController, PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { AlertService, ErrorCodes, LoadingIndicatorState, ToastDuration, ToastService } from '@shared';
import { LoggingExportService, LoggingService } from '@shared/logging';
import { first, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { ConnectionTypeEnum } from 'src/domain/connections/models';
import { ConnectionsManagerService } from 'src/domain/connections/services';
import { AppRoutingKeys } from '../../../../shared/constants/app-routing-keys';
import { ApiErrorModel } from '@api/http';
import { TokenProviderService } from '@api/authentication';

@Component({
	selector: 'app-export-popover',
	templateUrl: './export-popover.component.html',
	styleUrls: ['./export-popover.component.scss']
})
export class ExportPopoverComponent implements OnInit, OnDestroy {
	@Input() private workfileId: string;
	public isNoApiModeEnabled: boolean;

	private unsubscriber$: Subject<boolean> = new Subject<boolean>();

	public constructor(
		private navCtrl: NavController,
		private route: ActivatedRoute,
		private popoverController: PopoverController,
		private loggingExportService: LoggingExportService,
		private loggingService: LoggingService,
		private apiExporterService: ApiExporterService,
		private queryParamsProvider: QueryParamsProvider,
		private toastService: ToastService,
		private translateService: TranslateService,
		private businessLayerService: BusinessLayerService,
		private connectionsManager: ConnectionsManagerService,
		private workspaceRepository: WorkspaceRepository,
		private loadingIndicator: LoadingIndicatorState,
		private tokenProviderService: TokenProviderService,
		private alertService: AlertService
	) {}

	public async ngOnInit(): Promise<void> {
		this.businessLayerService
			.isNoApiModeEnabled$()
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe((isNoApi: boolean) => (this.isNoApiModeEnabled = isNoApi));
	}

	public ngOnDestroy() {
		this.unsubscriber$.next(true);
		this.unsubscriber$.unsubscribe();
	}

	public export(): void {
		console.log( "work archive, export workfield")
		this.popoverController.dismiss();
		this.navCtrl.navigateForward([`/${AppRoutingKeys.urlPathExport}/${this.workfileId}`], {
			relativeTo: this.route
		});
	}

	public async exportLogs(): Promise<void> {
		// return promise here and await
		this.loggingExportService.export();
		this.popoverController.dismiss();
	}

	public goToAbout(): void {
		this.navCtrl.navigateForward(AppRoutingKeys.urlPathAbout);
		this.popoverController.dismiss();
	}

	public goToHelpPage(): void {
		this.navCtrl.navigateForward(AppRoutingKeys.urlPathHelp);
		this.popoverController.dismiss();
	}

	public async logout(): Promise<void> {
		const cancelButton = {
			text: this.translateService.instant('popover.menu.logout.cancel')
		};

		const confirmButton = {
			text: this.translateService.instant('popover.menu.logout.confirm'),
			handler: async () => {
				this.popoverController.dismiss();

				await this.tokenProviderService.clearToken();
				await this.tokenProviderService.clearTokens();
				await this.navCtrl.navigateRoot(AppRoutingKeys.urlPathLogin);
			}
		};

		await this.alertService.presentAlert(
			this.translateService.instant('popover.menu.logout.alertTitle'),
			[cancelButton, confirmButton],
			false
		);
	}

	public goToSettings(): void {
		this.navCtrl.navigateForward(AppRoutingKeys.urlPathSettings);
		this.popoverController.dismiss();
	}

	public transferIn() {
		if (this.isNoApiModeEnabled) {
			this.toastService.presentError(
				this.translateService.instant('noApiMode.messages.error'),
				ToastDuration.Fast
			);
			return;
		}

		const workFileId = this.queryParamsProvider.workfileId;
		if (!workFileId) {
			this.toastService.presentError(
				this.translateService.instant('transferIn.errorMessages.noActiveWorkfile'),
				ToastDuration.Slow
			);
			return;
		}

		this.openConnectionsManager$(workFileId)
			.pipe(takeUntil(this.unsubscriber$), first())
			.subscribe(async (modal: IModal | null) => {
				if (modal?.data) {
					this.loggingService.info('ExportPopoverComponent', 'Set connection id');
					this.workspaceRepository.setWorkfileConnectionId(this.queryParamsProvider.workfileId, modal.data);
				} else {
					this.loggingService.info('ExportPopoverComponent', 'Start transfer in');
					this.transferInWorker(workFileId);
				}
			});
	}

	public goToConnectionsManager(): void {
		if (this.isNoApiModeEnabled) {
			this.toastService.presentError(
				this.translateService.instant('noApiMode.messages.error'),
				ToastDuration.Fast
			);
			return;
		}

		const workFileId = this.queryParamsProvider.workfileId;
		if (!workFileId) {
			this.toastService.presentError(
				this.translateService.instant('transferIn.errorMessages.noActiveWorkfile'),
				ToastDuration.Slow
			);
			return;
		}

		this.businessLayerService
			.getConnectionModel$(workFileId)
			.pipe(
				takeUntil(this.unsubscriber$),
				switchMap(() => this.connectionsManager.open(ConnectionTypeEnum.includeOptions))
			)
			.subscribe(async (modal: IModal | null) => {
				this.popoverController.dismiss();

				if (modal?.data) {
					this.loggingService.info('ConnectionsManagerPopoverComponent', 'Set connection id');
					this.workspaceRepository.setWorkfileConnectionId(this.queryParamsProvider.workfileId, modal.data);
				}
			});
	}

	private transferInWorker(workFileId: string) {
		this.loadingIndicator.setIsLoading(true);
		this.loggingService.info('ExportPopoverComponent', 'Start export to fusion ' + workFileId);
		this.apiExporterService
			.exportToFusion$(workFileId)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(
				async () => {
					this.popoverController.dismiss();
					this.loggingService.info('ExportPopoverComponent', 'Successful transfer in');
					this.toastService.presentSuccess(
						this.translateService.instant('transferIn.success'),
						ToastDuration.Fast
					);

					this.loadingIndicator.setIsLoading(false);

					try {
						await this.apiExporterService.archiveWorkifle(workFileId);
					} catch (error) {
						this.loggingService.error(ErrorCodes.TransferInFailed, error);

						this.toastService.presentError(
							this.translateService.instant('transferIn.errorMessages.generic'),
							ToastDuration.Slow
						);
					}
				},
				(error: ApiErrorModel) => {
					this.loadingIndicator.setIsLoading(false);
					this.loggingService.error(ErrorCodes.TransferInFailed, error);

					this.popoverController.dismiss();
					this.toastService.presentError(
						this.translateService.instant(
							`Something went wrong (error ${error.apiErrorCode} - ${error.errorMessage}).
							Please export the logs to retrieve the error.`
						),
						ToastDuration.Slow
					);
				}
			);
	}

	private openConnectionsManager$(workfileId: string): Observable<IModal | null> {
		return this.businessLayerService.getConnectionModel$(workfileId).pipe(
			switchMap((apiConnectionModel?: ApiConnectionsModel) => {
				if (!apiConnectionModel?.selectedOptions) {
					return this.connectionsManager.open(ConnectionTypeEnum.includeOptions);
				} else {
					return of(null);
				}
			})
		);
	}
}
